import React from 'react'
import TalentAppStep from "../components/TalentAppStep";
import ApplicationStartStep from "./ApplicationStartStep";
import TermsModal from "../../../components/TermsModal";
import Modal from "../../../components/Modal";
import InternationPhoneInput from "../../../components/form/InternationPhoneInput";
import Form from "../../../components/form/Form";
import { isPossiblePhoneNumber } from 'react-phone-number-input'
import ResumeRequirementCard from "../components/resume-verification/ResumeRequirementCard";
import { TalentAppCard } from '../components/TalentAppCard';


export default class InfoStep extends TalentAppStep {

    static path = '/basic-info';
    static LastUpdatedMethod = 'lastUpdatedInfo';

    /**
     * The title shown on the form
     */
    static StepTitle = TB.trans('talent.basic_info.step_title');
    /**
     * The subtitle shown on the form
     */
    static StepSubtitle = TB.trans('talent.basic_info.step_subtitle');
    /**
     * The start page info tile
     */
    static StepInfo = TB.trans('talent.basic_info.step_info');
    /**
     * The Step Section to be grouped in after application approval
     */
    static StepSection = 'Background';

    /**
     * The step needed before editing
     */
    static StepNeedsValidation(user) {

        if (!user.validateInfo()) {
            return ApplicationStartStep;
        }

        return false;
    };


    static hasCompleted(user) {
        return user.validateInfo()
    }

    /**
     * Check if we should prevent application from saving and show alert
     */
    static async getValidProps(user) {
        if (!user.accepted_terms) {
            await TB.sysAlert("talent.must_accept_terms");
            return false;
        }
        return user.validateInfo()
    };

    constructor(props) {
        super(props);
        this.nestedFormRefs = [React.createRef()];
        this.modalRef = React.createRef();
        this.resumeCardRef = React.createRef();
    }

    /**
     * Self-serve talent must upload a resume our AI can read. Supplier
     * talent resources (and admins editing them) keep the plain upload.
     */
    usesResumeVerification() {
        return !this.props.isAdminEditing
            && !this.props.user.isTalentSupplierResource();
    }

    /**
     * Also stop on a missing resume, since the verified upload lives
     * outside the step's form.
     */
    invalidFields() {
        const invalid = super.invalidFields();
        if (invalid || !this.usesResumeVerification() || this.props.user.resume_path) {
            return invalid;
        }

        this.setState({ resumeMissing: true });

        const card = this.resumeCardRef.current;
        if (card) {
            $('html, body').animate({ scrollTop: $(card).offset().top - 20 }, 1000);
        }
        return card || true;
    }

    onResumeVerified = (verifiedUser) => {
        this.setState({ resumeMissing: false });

        // The new resume is already saved. Merge only what changed with it
        // so unsaved edits on this step are kept.
        return this.props.onChange(TB.pick(verifiedUser, [
            'resume_path',
            'resume_verified_at',
            'generated_account_data',
            'talent_disciplines',
        ]));
    };


    /**
     * @param {User} user
     * @returns {Object}
     */
    static reviewMapping(user) {
        return {
            Name: user.fullName(),
            Location: user.location?.full,
            [TB.trans('talent.basic_info.review_mapping.contact_information_label')]: {
                [TB.trans('talent.basic_info.review_mapping.contact_information_phone_number_label')]: user.phoneNumber(),
                [TB.trans('talent.basic_info.review_mapping.contact_information_email_label')]: user.email
            },
            [TB.trans('talent.basic_info.review_mapping.contract_type_label')]: user.is_business ? {
                [TB.trans('talent.basic_info.review_mapping.contract_type_company_label')]: user.business_name + '\n' + user.business_type,
            } : {
                [TB.trans('talent.basic_info.review_mapping.contract_type_individual_label')]: user.fullName()
            },
            [TB.trans('talent.basic_info.review_mapping.language_proficiency_label')]: {
                [TB.trans('talent.basic_info.review_mapping.language_proficiency_primary_language_label')]: user.primary_language,
                [TB.trans('talent.basic_info.review_mapping.language_proficiency_english_level_label')]: laravel.talent_questions.english_proficiency_levels[user.english_level]
            },
            [TB.trans('talent.basic_info.review_mapping.referral_label')]: laravel.platform_referral_source_options[user.referred_by]
        }
    }

    componentDidMount() {
        super.componentDidMount();

        if (!TB.isDefined(this.props.user.accepted_terms) && this.props.user.validateInfo?.()) {
            this.props.onChange({
                accepted_terms: true
            })
        }
    }

    onChange(params, hasChanged) {
        if (!params.is_business && (params.business_name || params.business_type)) {
            params = {
                ...params,
                business_name: undefined,
                business_type: undefined
            };
        }

        return super.onChange(params);
    }

    /**
     * The fields mapping for the form
     */
    formFields = () => {
        const isSupplier = this.props.user.isTalentSupplierResource();

        let formFields = [
            {
                style: styles.fieldSection,
                fields: {
                    first_name: {
                        label: TB.trans('talent.basic_info.first_name_label'),
                        placeholder: TB.trans('talent.basic_info.first_name_placeholder'),
                        required: true,
                        maxLength: 46,
                        columns: 5,
                        style: styles.field,
                    },
                    last_name: {
                        label: TB.trans('talent.basic_info.last_name_label'),
                        placeholder: TB.trans('talent.basic_info.last_name_placeholder'),
                        required: true,
                        maxLength: 40,
                        columns: 5,
                        style: styles.field,
                    },
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    location: {
                        type: 'location',
                        includeAddress: true,
                        required: true,
                        columns: 10,
                        style: styles.field,
                    },
                }
            },
        ];

        if (!isSupplier) {
            formFields.push({
                style: styles.fieldSection,
                fields: {
                    referred_by: {
                        type: 'select',
                        label: TB.trans('talent.basic_info.referred_by_label'),
                        options: laravel.platform_referral_source_options,
                        placeholder: TB.trans('talent.basic_info.referred_by_placeholder'),
                        style: styles.field,
                        columns: 5,
                    },
                    referred_by_name: {
                        type: 'text',
                        maxLength: 255,
                        label: "Who referred you?",
                        required: false,
                        placeholder: "Name",
                        style: styles.field,
                        columns: 5,
                    },
                }
            },
                {
                    style: styles.fieldSection,
                    fields: {
                        has_spoken_with_wripple: {
                            type: 'ant-switch',
                            onLabel: 'Yes',
                            offLabel: 'No',
                            required: false,
                            label: 'Have you already spoken with someone at Wripple?',
                            style: { ...styles.field, marginBottom: 12 },
                            columns: 6,
                        },
                        ...(this.props.user.has_spoken_with_wripple && {
                            spoke_with_employee_named: {
                                type: 'text',
                                maxLength: 100,
                                label: "Who did you speak with?",
                                required: this.props.user.has_spoken_with_wripple,
                                placeholder: "Wripple employee's name",
                                style: { ...styles.field, marginBottom: 0 },
                                columns: 4,
                            },
                        })
                    }
                }
            );
        }

        formFields = formFields.concat([
            {
                fields: {
                    spacer: (
                        <div style={{ flexBasis: '100%' }}>
                            <h5 className="form-header">{TB.trans('talent.basic_info.contact_info_header')}</h5>
                        </div>
                    )
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    email: {
                        type: 'email',
                        label: TB.trans('talent.basic_info.email_label'),
                        placeholder: TB.trans('talent.basic_info.email_placeholder'),
                        maxLength: 255,
                        style: styles.field,
                        columns: 6,
                    },
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    phone: {
                        type: 'antd-input',
                        invalidStyles: { top: '100%' },
                        children: (
                            <InternationPhoneInput />
                        ),
                        validation: {
                            rules: {
                                isPossiblePhoneNumber: function (model, value) {
                                    if (TB.isString(value) && value?.length > 0 && !isPossiblePhoneNumber(value)) {
                                        return "Invalid phone number";
                                    }
                                    return true
                                }
                            },
                            errors: {
                                isDefaultRequiredValue: 'This field is required'
                            }
                        },
                        label: TB.trans('talent.basic_info.phone_number'),
                        minLength: 12,
                        style: styles.field,
                        columns: 6,
                    },
                }
            },
            {
                fields: {
                    spacer: (
                        <div style={{ flexBasis: '100%' }}>
                            <h5 className="form-header">{TB.trans('talent.basic_info.language_header')}</h5>
                        </div>
                    )
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    primary_language: {
                        type: 'select',
                        label: TB.trans('talent.basic_info.primary_language_label'),
                        placeholder: TB.trans('talent.basic_info.primary_language_placeholder'),
                        options: {
                            'English': TB.trans('talent.basic_info.lang_opt_english'),
                            'Spanish': TB.trans('talent.basic_info.lang_opt_spanish'),
                            'French': TB.trans('talent.basic_info.lang_opt_french'),
                            'Italian': TB.trans('talent.basic_info.lang_opt_italian'),
                            'Other': TB.trans('talent.basic_info.lang_opt_other')
                        },
                        style: styles.field,
                        columns: 5,
                    },
                    english_level: {
                        type: 'select',
                        label: TB.trans('talent.basic_info.english_level_label'),
                        placeholder: TB.trans('talent.basic_info.english_level_placeholder'),
                        options: laravel.talent_questions.english_proficiency_levels,
                        style: styles.field,
                        columns: 5,
                    },
                }
            },
            {
                fields: {
                    spacer: (
                        <div style={{ flexBasis: '100%' }}>
                            <h5 className="form-header">{TB.trans('talent.basic_info.diversity_header')}</h5>
                            <p className="form-subheader">{TB.trans('talent.basic_info.diversity_subheader')}</p>
                        </div>
                    )
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    ethnicity: {
                        type: 'select',
                        label: TB.trans('talent.basic_info.ethnicity_label'),
                        placeholder: TB.trans('talent.basic_info.ethnicity_placeholder'),
                        options: TB.reduce(laravel.talent_questions.ethnicity_options, (result, option) => {
                            result[option] = option;
                            return result;
                        }, {}),
                        required: false,
                        style: styles.field,
                        columns: 6,
                    },
                    ...(this.props.user.ethnicity === 'Other (Please specify)' ? {
                        ethnicity_option: {
                            type: 'text',
                            label: 'Please specify',
                            placeholder: "Specify here",
                            columns: 4,
                            required: this.props.user.ethnicity === 'Other (Please specify)',
                            style: styles.field,
                            validationErrors: {
                                isDefaultRequiredValue: 'This field is required',
                            }
                        }
                    } : {})
                },
            },
            {
                style: styles.fieldSection,
                fields: {
                    race: {
                        type: 'select',
                        label: TB.trans('talent.basic_info.race_label'),
                        placeholder: TB.trans('talent.basic_info.race_placeholder'),
                        options: TB.reduce(laravel.talent_questions.race_options, (result, option) => {
                            result[option] = option;
                            return result;
                        }, {}),
                        required: false,
                        style: styles.field,
                        columns: 6
                    },
                    ...(this.props.user.race === 'Other (Please specify)' ? {
                        race_option: {
                            type: 'text',
                            label: 'Please specify',
                            placeholder: "Specify here",
                            required: this.props.user.race === 'Other (Please specify)',
                            style: styles.field,
                            columns: 4,
                            validationErrors: {
                                isDefaultRequiredValue: 'This field is required',
                            },
                        }
                    } : {}),
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    gender: {
                        type: 'select',
                        label: TB.trans('talent.basic_info.gender_label'),
                        placeholder: TB.trans('talent.basic_info.gender_placeholder'),
                        options: TB.reduce(laravel.talent_questions.gender_options, (result, option) => {
                            result[option] = option;
                            return result;
                        }, {}),
                        required: false,
                        style: styles.field,
                        columns: 6,
                    },
                }
            },
            {
                style: styles.fieldSection,
                fields: {
                    preferred_pronoun: {
                        type: 'select',
                        label: TB.trans('talent.basic_info.preferred_pronoun_label'),
                        placeholder: TB.trans('talent.basic_info.preferred_pronoun_placeholder'),
                        options: TB.reduce(laravel.talent_questions.preferred_pronoun_options, (result, option) => {
                            result[option] = option;
                            return result;
                        }, {}),
                        required: false,
                        style: styles.field,
                        columns: 6,
                    },
                }
            },
        ])

        if (!isSupplier) {
            formFields.push(
                {
                    fields: {
                        spacer: (
                            <div style={{ flexBasis: '100%' }}>
                                <h5 className="form-header">{TB.trans('talent.basic_info.business_info_header')}</h5>
                            </div>
                        )
                    }
                },
                {
                    style: styles.fieldSection,
                    fields: {
                        is_business: {
                            type: 'switch',
                            onLabel: TB.trans('talent.basic_info.is_business_on_label'),
                            offLabel: TB.trans('talent.basic_info.is_business_off_label'),
                            label: TB.trans('talent.basic_info.is_business_label'),
                            style: styles.field,
                            columns: 8,
                        },
                    }
                }
            )
        }

        if (!isSupplier && this.props.user.is_business) {
            formFields.push({
                style: styles.fieldSection,
                fields: {
                    business_name: {
                        label: TB.trans('talent.basic_info.business_name_label'),
                        placeholder: TB.trans('talent.basic_info.business_name_placeholder'),
                        maxLength: 255,
                        style: styles.field,
                        columns: 5,
                    },
                    business_type: {
                        label: TB.trans('talent.basic_info.business_type_label'),
                        maxLength: 255,
                        placeholder: TB.trans('talent.basic_info.business_type_placeholder'),
                        style: styles.field,
                        columns: 5,
                    },
                }
            })
        }

        if (!isSupplier) {
            formFields.push({
                style: styles.fieldSection,
                fields: {
                    available_hours_per_week: {
                        label: TB.trans('talent.basic_info.available_hours_per_week_label'),
                        type: 'number',
                        required: true,
                        validation: {
                            rules: {
                                availableHoursValidation: function (model, value) {
                                    let min = parseInt(value);
                                    if (TB.isNumber(min) && min < 1) {
                                        return "Must be greater than 0";
                                    } else if (TB.isNumber(min) && min > 40) {
                                        return "Must be less than than 40";
                                    }
                                    return true
                                },
                                isInt: true
                            },
                            errors: {
                                isInt: TB.trans('validation.numeric', {
                                    'attribute': 'available hours per week'
                                }),
                                isDefaultRequiredValue: 'This field is required'
                            }
                        },
                        style: styles.field,
                        columns: 5,
                    },
                }
            }, {
                style: styles.fieldSection,
                fields: {
                    min_hourly_rate: {
                        label: TB.trans('talent.basic_info.min_hourly_rate_label'),
                        type: 'number',
                        addonBefore: "$",
                        placeholder: TB.trans('talent.basic_info.min_hourly_rate_placeholder'),
                        required: true,
                        disabled: this.props.disabled,
                        validationErrors: {
                            isDefaultRequiredValue: TB.trans('talent.basic_info.min_hourly_rate_validation_errors.missing_hourly_rate'),
                        },
                        style: styles.field,
                        columns: 5,
                    },
                    ideal_hourly_rate: {
                        type: 'number',
                        label: TB.trans('talent.basic_info.ideal_hourly_rate_label'),
                        addonBefore: "$",
                        placeholder: TB.trans('talent.basic_info.ideal_hourly_rate_placeholder'),
                        required: true,
                        disabled: this.props.disabled,
                        validationErrors: {
                            isDefaultRequiredValue: TB.trans('talent.basic_info.ideal_hourly_rate_validation_errors.missing_hourly_rate'),
                        },
                        style: styles.field,
                        columns: 5
                    },
                }
            }
            )
        }

        return formFields;
    };

    /**
     * Plain file input, kept for supplier talent resources.
     */
    renderResumeUploadField() {
        return (
            <div className="talent-role-form mb-4">
                <Form
                    ref={this.nestedFormRefs[0]}
                    onSubmit={null}
                    onChange={model => this.onChange(model)}
                    minimal
                    submitButton={false}
                    cancelButton={false}
                    disabled={this.props.isDisabled}
                    model={TB.pick(this.props.user, ['resume_path'])}
                    fields={[{
                        style: styles.fieldSection,
                        fields: {
                            resume_path: {
                                type: 'file',
                                required: true,
                                size: this.props.user?.resumeDocument()?.size,
                                viewable: typeof this.props.user?.resume_path === 'string',
                                label: 'Upload a PDF version of your resume (Required)',
                                fileNameDisplay: (fileData) => {
                                    return this.props.user?.resumeDocument()?.name || fileData.name;
                                },
                                validationErrors: {
                                    isDefaultRequiredValue: 'Resume Upload is required'
                                },
                                style: styles.field,
                                columns: 10
                            },
                        }
                    },]}>

                </Form>
            </div>
        );
    }

    renderFormFooter() {
        const { client_invites_received, is_on_talent_marketplace, accepted_terms } = this.props.user;

        return (
            <div className="flex flex-col gap-6 pt-6">
                {this.usesResumeVerification() ? (
                    <ResumeRequirementCard
                        ref={this.resumeCardRef}
                        user={this.props.user}
                        disabled={this.props.disabled}
                        showMissingError={!!this.state?.resumeMissing}
                        onVerified={this.onResumeVerified}
                    />
                ) : this.renderResumeUploadField()}

                {this.props.user.isClientInvitedResource() && (
                    <TalentAppCard>
                        <TalentAppCard.Section className="terms-panel">
                            <div>
                                <h5 className="form-header pl-0 m-0 pb-2">
                                    Join the Wripple Marketplace?
                                </h5>

                                <p className="form-subheader">
                                    You've received an invitation from <span className="font-bold">{client_invites_received[0].client.name}</span> to submit proposals for their jobs. Would you also be interested in joining the Wripple marketplace? This would grant you access to view the Wripple job board and submit proposals for matching jobs.
                                </p>

                                {/* <p className="form-subheader">
                                (Please note: You'll need to apply and undergo an interview process for approval by a Talent Team Member.)
                            </p> */}
                            </div>

                            <div className="form-checkbox mt-4">
                                <input
                                    type="checkbox"
                                    disabled={this.props.disabled}
                                    className="form-check-input"
                                    id="is_on_talent_marketplace"
                                    name="is_on_talent_marketplace"
                                    onChange={event => {
                                        this.props.onChange({
                                            is_on_talent_marketplace: !is_on_talent_marketplace
                                        })
                                    }}
                                    checked={!!is_on_talent_marketplace}
                                />

                                <label className="form-check-label" htmlFor="is_on_talent_marketplace">
                                    I would like to join the Wripple Marketplace
                                </label>
                            </div>
                        </TalentAppCard.Section>
                    </TalentAppCard>
                )}

                <TalentAppCard>
                    <TalentAppCard.Section className="terms-panel">
                        <h5 className="form-header pl-0 m-0 pb-2">
                            {TB.trans('talent.basic_info.terms_agreement_header')} <span className="text-[#E54963]">*</span>
                        </h5>

                        <div className="form-checkbox">
                            <input
                                type="checkbox"
                                disabled={this.props.disabled}
                                className="form-check-input"
                                id={"terms-checkbox"}
                                name={"terms-checkbox"}
                                onChange={event => {
                                    this.props.onChange({
                                        accepted_terms: !accepted_terms
                                    })
                                }}
                                checked={!!accepted_terms}
                            />

                            <label className="form-check-label" htmlFor={"terms-checkbox"}>
                                {TB.trans('talent.basic_info.terms_agreement_check_label')}&nbsp;
                                <span className="btn-link" onClick={e => {
                                    e.preventDefault();
                                    this.modalRef.current.showModal({
                                        title: 'Marketplace Terms of Use',
                                        defaultScroll: false
                                    })
                                }}>
                                    {TB.trans('talent.basic_info.terms_agreement_check_label_link')}
                                </span>
                            </label>
                        </div>
                    </TalentAppCard.Section>

                </TalentAppCard>


                <Modal ref={this.modalRef}>
                    <TermsModal userType="talent" onClose={() => this.modalRef.current.hideModal()} />
                </Modal>
            </div>
        )
    }
}

const styles = {
    fieldSection: {
        flexDirection: 'row'
    },
    field: {
        padding: '0 15px',
    },
    fieldHidden: {
        height: 0,
        width: 0,
        zIndex: -10,
        opacity: 0,
        position: 'absolute',
        left: -9999
    }
};